import { Card, Progress, Table, Tag, Collapse, Typography, Space, Empty } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PictureOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  AppstoreOutlined,
  TagsOutlined,
  SafetyCertificateOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Product, QualityMetrics } from '../../types/product';
import { ProviderTag } from '../common/ProviderTag';

const { Title, Text } = Typography;

interface QualityScoreTabProps {
  product: Product;
}

interface ProviderQualityData {
  provider: string;
  productId: string;
  score: number;
  metrics?: QualityMetrics;
  isCurrent: boolean;
}

export function QualityScoreTab({ product }: QualityScoreTabProps) {
  // Gather quality data from current provider and alternatives
  const providerData: ProviderQualityData[] = [
    {
      provider: product.data_source,
      productId: product.productid,
      score: product.quality_score,
      metrics: product.quality_metrics,
      isCurrent: true,
    },
  ];

  // Add alternatives
  if (product._alternatives) {
    for (const alt of product._alternatives) {
      providerData.push({
        provider: alt.provider,
        productId: alt.product_id,
        score: alt.quality_score,
        metrics: undefined, // Alternatives don't include full metrics
        isCurrent: false,
      });
    }
  }

  // Sort by score descending
  providerData.sort((a, b) => b.score - a.score);

  const getScoreColor = (score: number) => {
    if (score >= 70) return '#52c41a'; // green
    if (score >= 40) return '#faad14'; // yellow
    return '#ff4d4f'; // red
  };

  const getMetricIcon = (metric: string) => {
    const lower = metric.toLowerCase();
    if (lower.includes('image')) return <PictureOutlined />;
    if (lower.includes('description')) return <FileTextOutlined />;
    if (lower.includes('video')) return <VideoCameraOutlined />;
    if (lower.includes('pdf')) return <FilePdfOutlined />;
    if (lower.includes('3d')) return <AppstoreOutlined />;
    if (lower.includes('features')) return <TagsOutlined />;
    if (lower.includes('attributes')) return <TagsOutlined />;
    if (lower.includes('warranty')) return <SafetyCertificateOutlined />;
    if (lower.includes('related')) return <LinkOutlined />;
    return <TagsOutlined />;
  };

  const columns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric',
      render: (text: string) => (
        <Space>
          {getMetricIcon(text)}
          {text}
        </Space>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: 100,
      render: (points: number) => {
        const hasPoints = points > 0;
        return (
          <Space>
            {hasPoints ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            )}
            <Text style={{ color: hasPoints ? '#52c41a' : '#999' }}>
              {points}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Max',
      dataIndex: 'max',
      key: 'max',
      width: 80,
      render: (max: number) => <Text type="secondary">{max}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: unknown, record: { points: number; max: number }) => {
        const percentage = record.max > 0 ? (record.points / record.max) * 100 : 0;
        if (percentage >= 100) return <Tag color="green">Complete</Tag>;
        if (percentage > 0) return <Tag color="orange">Partial</Tag>;
        return <Tag color="red">Missing</Tag>;
      },
    },
  ];

  const renderProviderPanel = (data: ProviderQualityData) => {
    const scoreColor = getScoreColor(data.score);

    return (
      <Card
        key={data.productId}
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <ProviderTag provider={data.provider as Product['data_source']} />
            {data.isCurrent && <Tag color="blue">Current</Tag>}
            <Text type="secondary">ID: {data.productId}</Text>
          </Space>
        }
        extra={
          <Text strong style={{ fontSize: 18, color: scoreColor }}>
            {data.score.toFixed(1)} / 100
          </Text>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Progress
            percent={data.score}
            strokeColor={scoreColor}
            showInfo={false}
            size="small"
          />
        </div>

        {data.metrics ? (
          <>
            {/* Score Breakdown Table */}
            <Title level={5} style={{ marginBottom: 12 }}>
              Score Breakdown
            </Title>
            <Table
              dataSource={data.metrics.breakdown.map((item, idx) => ({
                ...item,
                key: idx,
              }))}
              columns={columns}
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong style={{ color: scoreColor }}>
                        {data.metrics!.score.toFixed(1)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <Text strong type="secondary">
                        {data.metrics!.max_score}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Progress
                        percent={data.metrics!.score}
                        steps={5}
                        size="small"
                        strokeColor={scoreColor}
                      />
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            {/* Raw Data Summary */}
            <Collapse
              ghost
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'raw',
                  label: 'Raw Metrics Data',
                  children: (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 8,
                      }}
                    >
                      <div>
                        <Text type="secondary">Images: </Text>
                        <Text>{data.metrics.raw.image_count}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Features: </Text>
                        <Text>{data.metrics.raw.feature_count}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Attributes: </Text>
                        <Text>{data.metrics.raw.attribute_count}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Has Video: </Text>
                        <Text>{data.metrics.raw.has_video ? 'Yes' : 'No'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Has PDF: </Text>
                        <Text>{data.metrics.raw.has_pdf ? 'Yes' : 'No'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Has 3D: </Text>
                        <Text>{data.metrics.raw.has_3d ? 'Yes' : 'No'}</Text>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </>
        ) : (
          <Empty
            description="Detailed metrics not available for alternative providers"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    );
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>
        Quality Score Analysis
      </Title>

      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Quality scores are calculated based on data completeness. Higher scores
        indicate more complete product information including images, descriptions,
        features, and attributes.
      </Text>

      {/* Provider Comparison Summary */}
      {providerData.length > 1 && (
        <Card size="small" style={{ marginBottom: 24 }}>
          <Title level={5}>Provider Comparison</Title>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 24,
              alignItems: 'center',
            }}
          >
            {providerData.map((data) => (
              <div key={data.productId} style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={data.score}
                  strokeColor={getScoreColor(data.score)}
                  size={80}
                  format={(percent) => `${percent?.toFixed(0)}`}
                />
                <div style={{ marginTop: 8 }}>
                  <ProviderTag provider={data.provider as Product['data_source']} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Individual Provider Panels */}
      {providerData.map(renderProviderPanel)}
    </div>
  );
}
